import type { AbilityGrade, DevelopmentProgramOutcome, DevelopmentProgress, GameState, Player, PlayerDevelopmentState, TeamSelection } from './types'
import { staffDevelopmentEfficiency, staffTacticalCohesionMultiplier } from './staffMarket'

const GRADE_ORDER: AbilityGrade[] = ['Limited', 'Below Standard', 'League Standard', 'Above Standard', 'Strong', 'Elite', 'Exceptional']

const FOCUS_TARGETS: Partial<Record<string, keyof Player['abilities']>> = {
  'tactical discipline': 'tacticalDiscipline', 'kicking accuracy': 'kicking', 'contested ball': 'contestedAbility',
  'defensive craft': 'defensiveCraft', 'defensive-wing retraining': 'positioning', 'goal sense': 'goalSense',
  'speed & acceleration': 'speed', 'endurance base': 'endurance', 'strength & power': 'strength',
  'decision making': 'decisionMaking', leadership: 'leadership', 'ruck work': 'ruckWork',
  'ground ball': 'groundBall', marking: 'marking', handball: 'handball', recovery: 'recovery', durability: 'durability',
}

const PROGRAM_NAMES: Record<string, string> = {
  'young-player-group': 'Emerging-player programme',
  'tactical-cohesion': 'Team tactical cohesion',
  'veteran-recovery': 'Veteran recovery programme',
}

const selectedPlayerIds = (team: TeamSelection) => new Set(
  Object.values(team).flat().filter(entry => entry.position !== 'EMG').map(entry => entry.playerId),
)

const isFocusRelevant = (player: Player, focus: string) => {
  const role = player.primaryRole.toLowerCase()
  if (['general development', 'tactical discipline', 'decision making', 'endurance base', 'strength & power', 'recovery', 'durability'].includes(focus)) return true
  if (focus.includes('defensive') || focus.includes('wing')) return role.includes('defender') || role.includes('wing')
  if (focus.includes('goal') || focus.includes('kicking')) return role.includes('forward')
  if (focus.includes('contested') || focus.includes('ground ball') || focus.includes('handball')) return role.includes('midfielder') || role.includes('ruck')
  if (focus.includes('ruck')) return role.includes('ruck')
  return true
}

export const assistantDevelopmentBonus = (state: GameState, player: Player, focus: string, assistantId?: string) => {
  const assistant = state.club.staff.find(member => member.id === assistantId)
  if (!assistant) return 0
  const lowerFocus = focus.toLowerCase()
  const role = player.primaryRole.toLowerCase()
  const relevant =
    (assistant.id === 'grant-havel' && (lowerFocus.includes('defensive') || lowerFocus.includes('marking') || role.includes('defender'))) ||
    (assistant.id === 'mara-keane' && (player.age <= 22 || ['contested', 'ground ball', 'handball', 'decision'].some(term => lowerFocus.includes(term)))) ||
    (assistant.id === 'ivo-sayer' && (role.includes('forward') || ['goal', 'kicking', 'marking'].some(term => lowerFocus.includes(term)))) ||
    (assistant.id === 'tessa-byrne' && ['speed', 'endurance', 'strength', 'recovery', 'durability'].some(term => lowerFocus.includes(term)))
  if (!relevant) return 0
  return assistant.workload >= 85 ? 1 : 2
}

const traitModifier = (grade: AbilityGrade) => {
  if (grade === 'Elite' || grade === 'Above Standard') return 1
  if (grade === 'Below Standard' || grade === 'Limited') return -1
  return 0
}

export const createPlayerDevelopmentState = (player: Pick<Player, 'name' | 'age' | 'developmentFocus'>): PlayerDevelopmentState => {
  const ari = player.name === 'Ari Dane'
  const theo = player.name === 'Theo Marr'
  return {
    focus: player.developmentFocus ?? 'general development',
    progress: 0,
    focusBanks: {},
    reviewWindow: ari ? { min: 18, max: 24 } : theo ? { min: 17, max: 23 } : { min: 22, max: 30 },
    internalThreshold: ari ? 22 : theo ? 20 : 26,
    potentialModifier: ari ? 0 : theo ? 1 : 0,
    nextAbility: ari ? 'tacticalDiscipline' : theo ? 'positioning' : 'decisionMaking',
    roleFamiliarity: ari ? 48 : Math.max(55, 82 - Math.max(0, player.age - 25)),
    staffConfidence: ari ? 'medium' : 'high',
    seniorMatches: 0,
    history: [],
  }
}

export const hydratePlayerDevelopment = (player: Player): Player => {
  const baseline = createPlayerDevelopmentState(player)
  const existing = player.development
  const focusBanks = existing?.focusBanks ?? (existing ? { [existing.focus]: existing.progress } : {})
  return {
    ...player,
    development: { ...baseline, ...existing, focusBanks, history: existing?.history ?? [] },
  }
}

const improveGrade = (grade: AbilityGrade): AbilityGrade => GRADE_ORDER[Math.min(GRADE_ORDER.length - 1, GRADE_ORDER.indexOf(grade) + 1)]

export const developmentGainFor = (state: GameState, playerId: string, _round: number, hasMatch = true) => {
  const allocation = state.staffPlan.development.find(entry => entry.playerId === playerId)
  const player = state.club.players.find(entry => entry.id === playerId)
  if (!allocation || !player || allocation.points <= 0) return 0

  const selected = selectedPlayerIds(state.staffPlan.team).has(playerId)
  const relevant = isFocusRelevant(player, allocation.focus)
  const base = Math.min(allocation.points, 3) * 2 + Math.max(0, allocation.points - 3)
  const youthBonus = player.age <= 21 ? 2 : player.age <= 24 ? 1 : 0
  const coachingBonus = assistantDevelopmentBonus(state, player, allocation.focus, allocation.assistant)
  // Baseline gains were calibrated around competent specialist support. A relevant assignment preserves
  // that rate; a poor fit or overloaded coach reduces it rather than creating unbounded bonus inflation.
  const coachingAdjustment = allocation.assistant ? coachingBonus - 2 : 0
  const characterBonus = traitModifier(player.abilities.coachability) + traitModifier(player.abilities.professionalism)
  const exposureBonus = hasMatch && selected ? relevant ? (player.development.seniorMatches === 0 ? 5 : 4) : 1 : 0
  const fatiguePenalty = player.fatigue >= 25 ? 2 : player.fatigue >= 19 ? 1 : 0
  const fitnessPenalty = player.fitness < 80 ? 1 : 0
  const rawGain = Math.max(0, base + youthBonus + coachingAdjustment + characterBonus + exposureBonus + player.development.potentialModifier - fatiguePenalty - fitnessPenalty)
  return Math.max(0, Math.round(rawGain * staffDevelopmentEfficiency(state, allocation.assistant)))
}

export const buildWeeklyDevelopmentOutcomes = (state: GameState, round: number, hasMatch = true): { players: DevelopmentProgress[]; programs: DevelopmentProgramOutcome[] } => {
  const selected = selectedPlayerIds(state.staffPlan.team)
  const players = state.staffPlan.development.flatMap(allocation => {
    const player = state.club.players.find(entry => entry.id === allocation.playerId)
    if (!player || allocation.points <= 0) return []
    const gain = developmentGainFor(state, player.id, round, hasMatch)
    const coachingBonus = assistantDevelopmentBonus(state, player, allocation.focus, allocation.assistant)
    const exposure = hasMatch && selected.has(player.id)
    const relevant = isFocusRelevant(player, allocation.focus)
    return [{
      player: player.name,
      focus: allocation.focus,
      pointsAllocated: allocation.points,
      progress: gain,
      matchExposure: exposure,
      note: exposure
        ? relevant
          ? `${allocation.points} allocated points combined with role-relevant senior exposure${coachingBonus ? ' and specialist coaching' : ''}.`
          : `${allocation.points} allocated points produced training progress; the senior role did not directly reinforce ${allocation.focus}.`
        : `${allocation.points} allocated points produced training progress without senior exposure${coachingBonus ? ' with specialist coaching support' : allocation.assistant ? '; the selected assistant had limited focus overlap' : ''}.`,
    }]
  })

  const programs = state.staffPlan.development.flatMap(allocation => {
    if (state.club.players.some(player => player.id === allocation.playerId) || allocation.points <= 0) return []
    const program = PROGRAM_NAMES[allocation.playerId] ?? allocation.playerId
    const effect = allocation.playerId === 'tactical-cohesion'
      ? `+${(allocation.points * 0.7).toFixed(1)} tactical cohesion on advancement`
      : allocation.playerId === 'veteran-recovery'
        ? `${allocation.points} points of recovery protection applied to the senior group`
        : `${allocation.points} points retained in the emerging-player pathway`
    return [{ program, pointsAllocated: allocation.points, effect, note: `The ${program.toLowerCase()} used its complete allocation; this capacity was not silently discarded.` }]
  })

  return { players, programs }
}

export const applyDevelopmentProgress = (players: Player[], updates: DevelopmentProgress[], round: number): Player[] => players.map(rawPlayer => {
  const player = hydratePlayerDevelopment(rawPlayer)
  const update = updates.find(entry => entry.player === player.name)
  if (!update) return player

  const current = player.development
  const banks = current.focusBanks ?? { [current.focus]: current.progress }
  const accumulated = (banks[update.focus] ?? 0) + update.progress
  const ability = FOCUS_TARGETS[update.focus] ?? current.nextAbility
  const from = player.abilities[ability]
  const upgraded = accumulated >= current.internalThreshold && from !== 'Exceptional'
  const to = upgraded ? improveGrade(from) : from
  const progress = upgraded ? accumulated - current.internalThreshold : accumulated
  const focusBanks = { ...banks, [update.focus]: progress }
  const matchExposure = update.matchExposure
  const historyEntry = {
    round,
    focus: update.focus,
    progressGained: update.progress,
    cumulativeProgress: progress,
    matchExposure,
    summary: upgraded
      ? `${update.note} Staff review confirmed ${String(ability).replace(/([A-Z])/g, ' $1').toLowerCase()} improved from ${from} to ${to}.`
      : update.note,
    ...(upgraded ? { upgrade: { ability, from, to } } : {}),
  }

  return {
    ...player,
    abilities: upgraded ? { ...player.abilities, [ability]: to } : { ...player.abilities },
    developmentFocus: update.focus,
    development: {
      ...current,
      focus: update.focus,
      progress,
      focusBanks,
      reviewWindow: upgraded ? { min: 20, max: 28 } : current.reviewWindow,
      internalThreshold: upgraded ? current.internalThreshold + 4 : current.internalThreshold,
      nextAbility: ability,
      roleFamiliarity: Math.min(100, current.roleFamiliarity + (matchExposure ? 6 : 2)),
      staffConfidence: upgraded || current.history.length >= 1 ? 'high' : current.staffConfidence,
      seniorMatches: current.seniorMatches + (matchExposure ? 1 : 0),
      history: [...current.history, historyEntry],
    },
    staffConcern: upgraded
      ? `${String(ability).replace(/([A-Z])/g, ' $1')} improved from ${from} to ${to}. Staff will now test whether the gain holds under match pressure.`
      : `${progress} progress banked toward the next staff review in ${update.focus}.`,
  }
})

export const tacticalCohesionGain = (state: GameState) => {
  const allocation = state.staffPlan.development.find(entry => entry.playerId === 'tactical-cohesion')
  return (allocation?.points ?? 0) * 0.7 * staffTacticalCohesionMultiplier(state)
}
